import { FileModel } from '@/models/file'
import { UserModel } from '@/models/user'
import { ProspectModel } from '@/models/prospect'
import { ForumThreadState } from '@/common/enums'
import { ForumReactionModel } from '@/models/forum/forumReaction'
import { ForumPollModel } from '@/models/forum/forumPoll'

export class ForumThreadModel {
  constructor(threadData = {}) {
    const {
      id,
      chatId,
      title,
      text,
      files,
      state,
      meta,
      createdAt,
      archivedAt,
      hiddenAt,
      user,
      prospect,
      themes,
      updatedAt,
      reactions,
      isAnonymous,
      anonymousState,
      anonymousId,
      poll,
      isPinned,
      isNew,
      hasPoll,
      isOwnerModerator,
      isAutotranslationEnabled,
      showResponseStatus,
      isSaved
    } = threadData || {}

    const {
      responseCount,
      newResponseCount,
      pollResponseCount,
      ownRepliesCount,
      ownResponseCount,
      ownPollResponseCount
    } = meta || {}

    this.id = id || null
    this.title = title || ''
    this.chatId = chatId || null
    this.text = text || ''
    this.state = state || 10
    this.files = Array.isArray(files) ? files.map((file) => new FileModel(file)) : []
    this.meta = {
      ownPollResponseCount: ownPollResponseCount || 0,
      responseCount: responseCount || 0,
      newResponseCount: newResponseCount || 0,
      pollResponseCount: pollResponseCount || 0,
      ownRepliesCount: ownRepliesCount || 0,
      ownResponseCount: ownResponseCount || 0
    }
    this.createdAt = createdAt || null
    this.archivedAt = archivedAt || null
    this.hiddenAt = hiddenAt || null
    this.updatedAt = updatedAt || null
    this.user = user ? new UserModel(user) : null
    this.prospect = prospect ? new ProspectModel(prospect) : null
    this.themes = Array.isArray(themes) ? [...themes] : []
    this.reactions = reactions?.map((reaction) => new ForumReactionModel(reaction)) || []
    this.isAnonymous = isAnonymous || false
    this.anonymousState = anonymousState || false
    this.anonymousId = anonymousId || null
    this.poll = poll ? new ForumPollModel(poll) : poll === null ? null : undefined
    this.isPinned = isPinned || false
    this.isNew = isNew || false
    this.hasPoll = hasPoll || false
    this.isOwnerModerator = isOwnerModerator ?? false
    this.isAutotranslationEnabled = isAutotranslationEnabled ?? false
    this.showResponseStatus = showResponseStatus ?? false
    this.isSaved = isSaved ?? false
  }

  static parseFromApi({ thread, chatId, users, prospects }) {
    return new ForumThreadModel({
      id: thread?.id,
      title: thread?.title,
      text: thread?.text,
      state: thread?.state,
      chatId,
      meta: {
        ownPollResponseCount: thread?.meta?.ownPollResponseCount,
        responseCount: thread?.meta?.responseCount,
        newResponseCount: thread?.meta?.newResponseCount,
        pollResponseCount: thread?.meta?.pollResponseCount,
        ownRepliesCount: thread?.meta?.ownRepliesCount,
        ownResponseCount: thread?.meta?.ownResponseCount
      },
      files:
        thread?.files?.map((file) => FileModel.parseFromApi({ file }))?.sort((a, b) => a?.order - b?.order) ||
        [],
      createdAt: thread?.createdAt,
      updatedAt: thread?.updatedAt,
      hiddenAt: thread?.hiddenAt,
      archivedAt: thread?.archivedAt,
      user: thread?.user ? UserModel.parseFromApi({ user: thread.user }) : null,
      prospect: thread?.prospect ? ProspectModel.parseFromApi({ prospect: thread.prospect }) : null,
      themes: Array.isArray(thread?.themes) ? [...thread.themes] : [],
      reactions:
        thread?.reactions?.map((reaction) =>
          ForumReactionModel.parseFromApi({ reaction, users, prospects })
        ) || [],
      isAnonymous: thread?.isAnonymous,
      anonymousState: thread?.anonymousState,
      anonymousId: thread?.anonymousId,
      poll: thread?.poll
        ? ForumPollModel.parseFromApi({ poll: thread?.poll, threadId: thread?.id, chatId })
        : thread.poll === null
          ? null
          : undefined,
      isPinned: thread?.isPinned,
      isNew: thread?.isNew,
      hasPoll: thread?.hasPoll,
      isOwnerModerator: thread?.isOwnerModerator,
      isAutotranslationEnabled: thread?.isAutotranslationEnabled,
      showResponseStatus: thread?.showResponseStatus,
      isSaved: thread?.isSaved
    })
  }

  setMetaFromApi({ meta }) {
    this.meta = {
      ownPollResponseCount: meta?.ownPollResponseCount,
      ownRepliesCount: meta?.ownRepliesCount,
      ownResponseCount: meta?.ownResponseCount,
      responseCount: meta?.responseCount,
      newResponseCount: meta?.newResponseCount,
      pollResponseCount: meta?.pollResponseCount
    }
  }

  convertToApi() {
    const { id, title, text, themes, isAnonymous, isPinned, showResponseStatus } = this

    let poll
    if (this.poll === null || this.poll === undefined) {
      poll = this.poll
    } else if (this.poll?.convertToApi) {
      poll = this.poll.convertToApi()
    } else {
      poll = undefined
    }

    return {
      id,
      title,
      text,
      fileIdList: this.files?.map((file) => file.id) || undefined,
      themes: [...themes],
      isAnonymous,
      poll,
      isPinned,
      showResponseStatus
    }
  }

  get isActive() {
    return this.state === ForumThreadState.ACTIVE
  }

  get isArchived() {
    return this.state === ForumThreadState.ARCHIVED
  }

  get isHidden() {
    return this.state === ForumThreadState.HIDDEN
  }
}
